// VAT Groups: page text and the list of countries. Generated file.
const config = {
  "slug": "vat-groups",
  "name": "VAT Groups",
  "menu": {
    "show": true,
    "label": "VAT Groups",
    "desc": "One VAT return for several legal entities"
  },
  "meta": {
    "title": "VAT Groups: One Return for Several Entities | Taxera",
    "description": "VAT groups explained: how several legal entities file one VAT return, which countries allow it, and how Taxera consolidates group VAT from your ERP."
  },
  "hero": {
    "h1": "VAT groups: one VAT return for several legal entities.",
    "h1Accent": "Prepared from your ERP, consolidated for you.",
    "text": "In some countries, related companies can join a VAT group and report together. Taxera prepares each member's figures from your ERP, consolidates them at group level, lets you adjust, and gives you one group total. Companies outside the group keep using the standard VAT return.",
    "secondaryCta": "See VAT groups by country"
  },
  "countriesSection": {
    "heading": "VAT group rules by country",
    "intro": "Not every country allows VAT groups, and the rules differ where they do. Pick a country to see how its VAT group works and what it means for your ERP.",
    "trackerPath": "/tracker/vat_structure"
  },
  "what": {
    "heading": "What is a VAT group, in plain words?",
    "paragraphs": [
      "A VAT group lets several related legal entities in the same country be treated as one for VAT. Each company keeps its own VAT ID for its own business, but the group reports together, in one VAT return with one total to pay or reclaim.",
      "Only some countries allow it, and the rules for who can join differ. It is also normal for a company to have part of its entities in the group and part outside it. The entities outside the group file their own VAT returns as usual."
    ],
    "explainer": {
      "heading": "How VAT groups and VAT returns fit together",
      "items": [
        {
          "icon": "Building2",
          "title": "Every member still has its own figures",
          "text": "Each company in the group has its own transactions, so its own figures are still prepared from its own ERP data, in the same way as a standard VAT return."
        },
        {
          "icon": "Layers",
          "title": "The group combines them",
          "text": "The members' figures are consolidated into one group return. Adjustments can be made at group level, and the result is one total."
        },
        {
          "icon": "Workflow",
          "title": "Some entities stay outside",
          "text": "A company can have entities in the VAT group and entities that are not. The ones outside it use the standard VAT return, in the same platform."
        }
      ]
    }
  },
  "steps": {
    "heading": "How Taxera handles VAT groups",
    "intro": "The same ERP integration that prepares standard VAT returns also handles the group, so you use one platform for both.",
    "items": [
      {
        "icon": "ListChecks",
        "title": "Set up the group",
        "text": "Choose which of your company codes belong to a VAT group. The other company codes stay on the standard VAT return."
      },
      {
        "icon": "Database",
        "title": "Prepare each member",
        "text": "For every member, Taxera extracts the data from your ERP and builds the member's VAT figures, exactly as it does for a standard return."
      },
      {
        "icon": "Layers",
        "title": "Consolidate the group",
        "text": "The members' figures are combined into the group return. Your team can make adjustments at group level."
      },
      {
        "icon": "CheckCircle2",
        "title": "Review the group total",
        "text": "You review the group total, with drill-down to each member and to the source transactions, before the group return is filed. Members in a group do not file individually."
      }
    ]
  },
  "benefits": {
    "heading": "What changes for your team",
    "items": [
      {
        "icon": "Layers",
        "title": "Standard and group in one place",
        "text": "Entities in a VAT group and entities outside it are handled in the same platform, from the same ERP data."
      },
      {
        "icon": "Scale",
        "title": "Group-level adjustments",
        "text": "Adjustments are made once, at group level, instead of being patched into spreadsheets after the fact."
      },
      {
        "icon": "Search",
        "title": "Drill-down to the source",
        "text": "From the group total, go to each member and from each member to the transactions behind it."
      },
      {
        "icon": "BellRing",
        "title": "Rule changes are our problem",
        "text": "VAT group rules change, and some countries are still introducing them. We track the changes so you do not have to."
      }
    ]
  },
  "faq": {
    "heading": "VAT groups, questions answered",
    "items": [
      {
        "q": "What is a VAT group?",
        "a": "A VAT group is a set of related legal entities in one country that are treated as a single taxable person for VAT. The group reports together in one VAT return, with one total to pay or reclaim."
      },
      {
        "q": "Are VAT groups the same as VAT returns?",
        "a": "No. A VAT return is the periodic report an entity files. A VAT group is a structure that lets several entities file one return together. Taxera handles both: standard VAT returns for entities outside a group, and consolidated group figures for entities inside one."
      },
      {
        "q": "Which countries allow VAT groups?",
        "a": "Many EU countries do, including Germany, France, the Netherlands, Belgium, Italy, Spain, Poland and Ireland, and the UK has its own regime. Some countries have only recently introduced them, and a few limit them to certain sectors. Check the country pages for details."
      },
      {
        "q": "Can a company have some entities in a VAT group and some outside it?",
        "a": "Yes. Entities that belong to the VAT group are configured as members, and the others keep using the standard VAT return. Taxera supports both side by side."
      },
      {
        "q": "Can an entity in a VAT group file its own return through Taxera?",
        "a": "No. A member of a VAT group reports through the group, so its figures are prepared in Taxera and consolidated into the group return instead of being filed individually."
      },
      {
        "q": "Can entities in different countries be in the same VAT group?",
        "a": "In general, no. VAT groups usually apply within one country, and some countries limit them to entities established there. The rules differ, so check the page for each country."
      }
    ]
  },
  "related": [
    {
      "to": "/solutions/vat-returns",
      "label": "VAT Returns",
      "note": "Standard returns for entities outside a group"
    },
    {
      "to": "/tracker/vat_structure",
      "label": "Live VAT group changes",
      "note": "Free Global Tax Tracker"
    },
    {
      "to": "/solutions/e-invoicing",
      "label": "e-Invoicing",
      "note": "Same data, same integration"
    },
    {
      "to": "/blog",
      "label": "Latest articles and updates",
      "note": "Regulatory analysis from our team"
    }
  ],
  "cta": {
    "heading": "See VAT groups running on your own ERP data",
    "text": "Book a short demo. We will show you how members are prepared and consolidated into one group return."
  },
  "country": {
    "eyebrow": "VAT groups {name}",
    "headingShort": "VAT groups in {name}: {short} explained",
    "headingPlain": "VAT groups in {name}, explained",
    "mandateLabel": "The regime",
    "whoLabel": "Who can join",
    "howLabel": "How it works",
    "datesHeading": "Key points and dates for {name}",
    "leftHeading": "What the group return covers",
    "rightHeading": "What your ERP has to deliver",
    "flowHeading": "The process, step by step",
    "linkedHeading": "Linked obligations in {name}",
    "faqHeading": "VAT groups {name}: questions, answered",
    "moreHeading": "More VAT group guides",
    "allLabel": "All VAT group countries",
    "otherLabel": "VAT groups {name}",
    "downloadTitle": "Keep this guide",
    "downloadText": "Download the {name} VAT groups guide as a PDF. It has the same content as this page.",
    "printTitle": "VAT groups guide: {name}",
    "trackerPath": "/tracker/vat_structure",
    "faqs": [
      {
        "q": "How do VAT groups work in {name}?",
        "a": [
          "mandateName",
          "summary"
        ]
      },
      {
        "q": "Who can join a VAT group in {name}?",
        "a": [
          "inScope"
        ]
      },
      {
        "q": "What are the key points and dates for VAT groups in {name}?",
        "a": [
          "keyDates"
        ]
      },
      {
        "q": "How is the VAT group return handled in {name}?",
        "a": [
          "process"
        ]
      },
      {
        "q": "What does a VAT group change in {name}?",
        "a": [
          "businessProcess"
        ]
      },
      {
        "q": "Where can I find the official rules for VAT groups in {name}?",
        "a": [
          "officialSources"
        ]
      }
    ]
  },
  "countries": [
    {
      "slug": "germany",
      "code": "DE",
      "name": "Germany",
      "shortName": "Organschaft",
      "region": "Europe",
      "status": "voluntary",
      "statusLabel": "Optional, automatic",
      "reviewed": "October 2026",
      "title": "VAT Group in Germany (Organschaft): Rules Explained | Taxera",
      "description": "How VAT groups work in Germany: who can join, key points, how the group return is built and what your ERP has to deliver."
    },
    {
      "slug": "france",
      "code": "FR",
      "name": "France",
      "shortName": "Assujetti unique",
      "region": "Europe",
      "status": "voluntary",
      "statusLabel": "Optional, by election",
      "reviewed": "October 2026",
      "title": "VAT Group in France: Rules Explained | Taxera",
      "description": "How VAT groups work in France: who can join, key points, how the group return is built and what your ERP has to deliver."
    },
    {
      "slug": "netherlands",
      "code": "NL",
      "name": "Netherlands",
      "shortName": "Fiscale eenheid",
      "region": "Europe",
      "status": "voluntary",
      "statusLabel": "Optional, automatic",
      "reviewed": "October 2026",
      "title": "VAT Group in Netherlands: Rules Explained | Taxera",
      "description": "How VAT groups work in Netherlands: who can join, key points, how the group return is built and what your ERP has to deliver."
    },
    {
      "slug": "belgium",
      "code": "BE",
      "name": "Belgium",
      "shortName": "VAT unit",
      "region": "Europe",
      "status": "voluntary",
      "statusLabel": "Optional, by application",
      "reviewed": "October 2026",
      "title": "VAT Group in Belgium (VAT unit): Rules Explained | Taxera",
      "description": "How VAT groups work in Belgium: who can join, key points, how the group return is built and what your ERP has to deliver."
    },
    {
      "slug": "italy",
      "code": "IT",
      "name": "Italy",
      "shortName": "Gruppo IVA",
      "region": "Europe",
      "status": "voluntary",
      "statusLabel": "Optional, binding",
      "reviewed": "October 2026",
      "title": "VAT Group in Italy (Gruppo IVA): Rules Explained | Taxera",
      "description": "How VAT groups work in Italy: who can join, key points, how the group return is built and what your ERP has to deliver."
    },
    {
      "slug": "spain",
      "code": "ES",
      "name": "Spain",
      "shortName": "REGE",
      "region": "Europe",
      "status": "voluntary",
      "statusLabel": "Optional, two regimes",
      "reviewed": "October 2026",
      "title": "VAT Group in Spain (REGE): Rules Explained | Taxera",
      "description": "How VAT groups work in Spain: who can join, key points, how the group return is built and what your ERP has to deliver."
    },
    {
      "slug": "poland",
      "code": "PL",
      "name": "Poland",
      "shortName": "Grupa VAT",
      "region": "Europe",
      "status": "voluntary",
      "statusLabel": "Optional, by agreement",
      "reviewed": "October 2026",
      "title": "VAT Group in Poland (Grupa VAT): Rules Explained | Taxera",
      "description": "How VAT groups work in Poland: who can join, key points, how the group return is built and what your ERP has to deliver."
    },
    {
      "slug": "ireland",
      "code": "IE",
      "name": "Ireland",
      "shortName": "",
      "region": "Europe",
      "status": "voluntary",
      "statusLabel": "Optional",
      "reviewed": "October 2026",
      "title": "VAT Group in Ireland: Rules Explained | Taxera",
      "description": "How VAT groups work in Ireland: who can join, key points, how the group return is built and what your ERP has to deliver."
    },
    {
      "slug": "luxembourg",
      "code": "LU",
      "name": "Luxembourg",
      "shortName": "Groupe TVA",
      "region": "Europe",
      "status": "voluntary",
      "statusLabel": "Optional",
      "reviewed": "October 2026",
      "title": "VAT Group in Luxembourg (Groupe TVA): Rules Explained | Taxera",
      "description": "How VAT groups work in Luxembourg: who can join, key points, how the group return is built and what your ERP has to deliver."
    },
    {
      "slug": "portugal",
      "code": "PT",
      "name": "Portugal",
      "shortName": "RGIVA",
      "region": "Europe",
      "status": "voluntary",
      "statusLabel": "New from 1 July 2026",
      "reviewed": "October 2026",
      "title": "VAT Group in Portugal (RGIVA): Rules Explained | Taxera",
      "description": "How VAT groups work in Portugal: who can join, key points, how the group return is built and what your ERP has to deliver."
    },
    {
      "slug": "romania",
      "code": "RO",
      "name": "Romania",
      "shortName": "Grup fiscal unic",
      "region": "Europe",
      "status": "voluntary",
      "statusLabel": "Optional",
      "reviewed": "October 2026",
      "title": "VAT Group in Romania: Rules Explained | Taxera",
      "description": "How VAT groups work in Romania: who can join, key points, how the group return is built and what your ERP has to deliver."
    },
    {
      "slug": "latvia",
      "code": "LV",
      "name": "Latvia",
      "shortName": "PVN grupa",
      "region": "Europe",
      "status": "voluntary",
      "statusLabel": "Optional",
      "reviewed": "October 2026",
      "title": "VAT Group in Latvia (PVN grupa): Rules Explained | Taxera",
      "description": "How VAT groups work in Latvia: who can join, key points, how the group return is built and what your ERP has to deliver."
    },
    {
      "slug": "malta",
      "code": "MT",
      "name": "Malta",
      "shortName": "",
      "region": "Europe",
      "status": "voluntary",
      "statusLabel": "Optional",
      "reviewed": "October 2026",
      "title": "VAT Group in Malta: Rules Explained | Taxera",
      "description": "How VAT groups work in Malta: who can join, key points, how the group return is built and what your ERP has to deliver."
    },
    {
      "slug": "czech-republic",
      "code": "CZ",
      "name": "Czech Republic",
      "shortName": "Skupina",
      "region": "Europe",
      "status": "voluntary",
      "statusLabel": "Optional, by application",
      "reviewed": "October 2026",
      "title": "VAT Group in Czech Republic: Rules Explained | Taxera",
      "description": "How VAT groups work in Czech Republic: who can join, key points, how the group return is built and what your ERP has to deliver."
    },
    {
      "slug": "united-kingdom",
      "code": "GB",
      "name": "United Kingdom",
      "shortName": "",
      "region": "Europe",
      "status": "voluntary",
      "statusLabel": "Optional",
      "reviewed": "October 2026",
      "title": "VAT Group in United Kingdom: Rules Explained | Taxera",
      "description": "How VAT groups work in United Kingdom: who can join, key points, how the group return is built and what your ERP has to deliver."
    },
    {
      "slug": "slovenia",
      "code": "SI",
      "name": "Slovenia",
      "shortName": "Skupina za DDV",
      "region": "Europe",
      "status": "voluntary",
      "statusLabel": "New from 1 January 2026",
      "reviewed": "October 2026",
      "title": "VAT Group in Slovenia: Rules Explained | Taxera",
      "description": "How VAT groups work in Slovenia: who can join, key points, how the group return is built and what your ERP has to deliver."
    }
  ]
};
export default config;
